import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { EmptyObject } from 'lib/object/objectUtils'
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { InView, useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { mod } from 'lib/maths/mathUtils'
import { scrollToLogicalPosition } from './utils'

const SCROLL_SNAP_RESTORE_FALLBACK_MS = 1200
const MAX_SMOOTH_REWIND_SLIDE_DISTANCE = 3

const Carousel = styled.div`
  display: flex;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: 1 0 100%;
    scroll-snap-align: center;
  }
`

const CarouselTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  height: 100%;
`

const OverlayLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const ExternalImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
`

const InternalImage = styled(ResponsiveImage)`
  height: 100%;
`

const SlideContainer = styled(InView)`
  position: relative;
`

export interface ImageCarouselRefInterface {
  /**
   * Returns the current index the carousel is on
   * Note: This may not always be an image as the index includes any provided interstitials
   */
  getCurrentImageIndex: () => number
  /**
   * Moves the carousel to the next image
   */
  nextImage: () => void
  /**
   * Moves the carousel to the previous image
   */
  previousImage: () => void
  /**
   * Moves the carousel to a specific image
   * Can choose if it should animate to the image or appear there instantly
   */
  goToImage: (image: App.Image, method?: 'smooth' | 'instant') => void
  goToIndex: (index: number, method?: 'smooth' | 'instant') => void
}

export interface ImageCarouselInterstitial {
  id: string
  position: number | 'start' | 'end'
  element: React.ReactNode
}

interface ImageCarouselItem {
  id: string
  position: number | 'start' | 'end'
  type: 'interstital' | 'image'
  element?: React.ReactNode
  image?: App.Image
  overlay?: React.ReactNode
}

export type ImageChangeHandler = (index: number, image?: App.Image) => void
export type ImageLoadHandler = (index: number, image?: App.Image) => void

interface Props {
  /** The index of the image to start the carousel on, defaults to first image */
  defaultIndex?: number
  images: Array<App.Image>
  interstitials?: Array<ImageCarouselInterstitial>
  className?: string
  imageParams?: ImageParams
  eagerLoadFirstImage?: boolean
  disablePlaceholders?: boolean
  carouselRef?: React.RefObject<ImageCarouselRefInterface | null>
  onImageChange?: ImageChangeHandler
  onImageLoad?: ImageLoadHandler
  /** Determines whether wrapping will happen instantly or smooth scroll back to the start/end. */
  wrapBehaviour?: 'instant' | 'rewind'
  /**
   * Adds the ability to overlay full elements on top of images in the carousel
   * You can do this by providing a record of image ids -> overlay components
   * The carousel will render the overlay component with the matching image id
   *
   * This will not overlay on an interstitial - you have full control there yourself
   */
  imageOverlays?: Record<string, React.ReactNode>
}

function BaseImageCarousel({
  images,
  className,
  imageParams = EmptyObject,
  eagerLoadFirstImage,
  carouselRef,
  disablePlaceholders,
  onImageChange = noop,
  onImageLoad = noop,
  interstitials = EmptyArray,
  defaultIndex = 0,
  imageOverlays = EmptyObject,
  wrapBehaviour = 'instant',
}: Props) {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(defaultIndex)
  const [seenImages, setSeenImages] = useState<Set<string>>(new Set())
  // Used only for delayed rewind callbacks to avoid duplicate onImageChange calls.
  const latestImageIndexRef = useRef(defaultIndex)
  // Holds the active rewind cleanup so new navigation can cancel stale timers/listeners.
  const rewindCleanupRef = useRef<(() => void) | undefined>(undefined)
  const isRewindingRef = useRef(false)
  const localCarouselRef = useRef<HTMLDivElement>(null)

  const imageItems = useMemo((): Array<ImageCarouselItem> => {
    const allItems = [
      ...interstitials.map((interstitial): ImageCarouselItem => ({
        id: interstitial.id,
        position: interstitial.position,
        element: interstitial.element,
        type: 'interstital',
      })),
      ...images.map((image, index): ImageCarouselItem => ({
        id: image.id ?? image.url ?? index.toString(),
        position: index + 1,
        image,
        type: 'image',
        overlay: imageOverlays[image.id ?? image.url ?? index.toString()],
      })),
    ]

    return sortBy(allItems, item => {
      if (item.position === 'end') {
        return 10000
      } else if (item.position === 'start') {
        return -1
      } else {
        return item.position
      }
    }, 'asc')
  }, [interstitials, images, imageOverlays])

  const changeImage = useCallback((index: number) => {
    const image = imageItems[index].image
    if (index !== latestImageIndexRef.current) {
      latestImageIndexRef.current = index
      setCurrentImageIndex(index)
      onImageChange(index, image)
    }
  }, [imageItems, onImageChange])

  const goToImage = useCallback((index: number, method: 'smooth' | 'instant' = 'instant') => {
    const carouselElement = localCarouselRef.current

    if (!carouselElement) return

    const offset = carouselElement.offsetWidth
    const nextLeft = offset * index

    scrollToLogicalPosition(carouselElement, {
      left: nextLeft,
      // @ts-ignore 'instant' is still supported by browsers and works as we intend it.
      // Auto would still sometimes smooth scroll it
      behavior: method,
    })
  }, [])

  const cleanupActiveRewind = useCallback(() => {
    rewindCleanupRef.current?.()
    isRewindingRef.current = false
  }, [])

  const temporarilyDisableScrollSnapForRewind = useCallback((carouselElement: HTMLDivElement, index: number) => {
    cleanupActiveRewind()

    const scrollSnapType = carouselElement.style.scrollSnapType
    carouselElement.style.scrollSnapType = 'none'
    isRewindingRef.current = true
    let hasRestoredScrollSnap = false

    function cleanupRewind() {
      if (hasRestoredScrollSnap) return
      hasRestoredScrollSnap = true
      window.clearTimeout(restoreScrollSnapFallbackTimer)
      carouselElement.removeEventListener('scrollend', handleScrollEnd)
      carouselElement.style.scrollSnapType = scrollSnapType
      isRewindingRef.current = false
      rewindCleanupRef.current = undefined
    }

    function handleScrollEnd() {
      cleanupRewind()
      changeImage(index)
    }

    const restoreScrollSnapFallbackTimer = window.setTimeout(handleScrollEnd, SCROLL_SNAP_RESTORE_FALLBACK_MS)

    rewindCleanupRef.current = cleanupRewind
    carouselElement.addEventListener('scrollend', handleScrollEnd, { once: true })
  }, [changeImage, cleanupActiveRewind])

  const rewindToImage = useCallback((index: number) => {
    const carouselElement = localCarouselRef.current

    if (!carouselElement) return

    temporarilyDisableScrollSnapForRewind(carouselElement, index)
    goToImage(index, 'smooth')
  }, [goToImage, temporarilyDisableScrollSnapForRewind])

  useEffect(() => {
    if (defaultIndex > 0) {
      goToImage(defaultIndex)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      rewindCleanupRef.current?.()
    }
  }, [])

  const moveSlide = useCallback((direction: 'previous' | 'next') => {
    if (imageItems.length === 0 || isRewindingRef.current) return

    const nextIndex = direction === 'previous' ? currentImageIndex - 1 : currentImageIndex + 1
    const willWrap = nextIndex < 0 || nextIndex >= imageItems.length
    const targetIndex = mod(nextIndex, imageItems.length)
    const rewindSlideDistance = Math.abs(targetIndex - currentImageIndex)
    const canSmoothRewind = rewindSlideDistance <= MAX_SMOOTH_REWIND_SLIDE_DISTANCE
    const willRewind = willWrap && wrapBehaviour === 'rewind' && canSmoothRewind
    const scrollMethod = willWrap && !willRewind ? 'instant' : 'smooth'

    if (willRewind) {
      rewindToImage(targetIndex)
    } else {
      cleanupActiveRewind()
      goToImage(targetIndex, scrollMethod)
    }

    if (willWrap && !willRewind) {
      changeImage(targetIndex)
    }
  }, [changeImage, cleanupActiveRewind, currentImageIndex, goToImage, imageItems.length, rewindToImage, wrapBehaviour])

  const loadImage = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    const index = parseInt(event.currentTarget.dataset.index ?? '0')
    const image = imageItems[index].image
    onImageLoad(index, image)
  }, [imageItems, onImageLoad])

  useImperativeHandle(carouselRef, () => ({
    getCurrentImageIndex: () => currentImageIndex,
    previousImage: () => moveSlide('previous'),
    nextImage: () => moveSlide('next'),
    goToImage: (image, method) => {
      const imageIndex = Math.max(imageItems.findIndex(item => item.image === image), 0)
      cleanupActiveRewind()
      goToImage(imageIndex, method)
      changeImage(imageIndex)
    },
    goToIndex: (index, method) => {
      const safeIndex = mod(index, imageItems.length)
      cleanupActiveRewind()
      goToImage(safeIndex, method)
      changeImage(safeIndex)
    },
  }))

  const onImageInViewChange = useCallback((inView: boolean, entry: IntersectionObserverEntry) => {
    if (inView) {
      const index = parseInt((entry.target as HTMLDivElement).dataset.index ?? '0')
      const image = imageItems[index].image
      setSeenImages((seenImages) => new Set([image?.id ?? image?.url ?? '', ...seenImages]))

      if (!isRewindingRef.current) {
        changeImage(index)
      }
    }
  }, [changeImage, imageItems])

  const [preloadIndexs, preloadImageIndex] = useMemo(() => {
    return [
      // allow preloading of images 2 behind/2 in front
      new Set([
        mod(currentImageIndex - 2, imageItems.length),
        mod(currentImageIndex - 1, imageItems.length),
        currentImageIndex,
        mod(currentImageIndex + 1, imageItems.length),
        mod(currentImageIndex + 2, imageItems.length),
      ]),
      // we preload the full image only one behind/in front
      new Set([
        mod(currentImageIndex - 1, imageItems.length),
        currentImageIndex,
        mod(currentImageIndex + 1, imageItems.length),
      ]),
    ]
  }, [currentImageIndex, imageItems.length])

  // turn off item intersection observers when the carousel itself isn't in view
  const [carouselInViewRef, carouselInView] = useInView({
    skip: imageItems.length === 0,
    initialInView: imageItems.length === 0,
    rootMargin: '100px 100px 100px 100px',
  })

  return <CarouselTrack ref={carouselInViewRef} className={className}>
    <Carousel ref={localCarouselRef}>
      {imageItems.map((item, index) => <SlideContainer
        onChange={onImageInViewChange}
        threshold={0.5}
        key={item.id}
        skip={
          // can't be seen, don't need to watch
          !carouselInView ||
          // not in preload bounds, turn it off
          !preloadIndexs.has(index)
        }
        data-index={index}
      >
        {item.type === 'interstital' && item.element}
        {item.type === 'image' && item.image && <>
          {(item.image.url && !item.image.id) && <ExternalImage
            src={item.image.url}
            alt={item.image.title}
            data-index={index}
            onLoad={loadImage}
          />}
          {item.image.id && <InternalImage
            {...imageParams}
            fit={imageParams.fit ?? 'center'}
            id={item.image.id}
            alt={item.image.title}
            data-index={index}
            loading={(eagerLoadFirstImage && index === 0) ? 'eager' : 'lazy-managed'}
            fetchPriority={(eagerLoadFirstImage && index === 0) ? 'high' : undefined}
            showImage={seenImages.has(item.image.id) || (carouselInView && preloadImageIndex.has(index))}
            disablePlaceholder={!!disablePlaceholders || !preloadIndexs.has(index)}
            onLoad={loadImage}
          />}
          {item.overlay && <OverlayLayer>
            {item.overlay}
          </OverlayLayer>}
        </>}
      </SlideContainer>)}
    </Carousel>
  </CarouselTrack>
}

export default React.memo(BaseImageCarousel)
